import { useEffect, useRef, useState } from 'react';

export type SmoothTypewriterOptions = {
  /** Wait after the first target characters arrive before showing anything. */
  startDelayMs?: number;
  /** Average ms per character (lower = faster). */
  msPerChar?: number;
  active: boolean;
  /** Unique ID for this stream — used to resume position after chat switch. */
  streamId?: string;
  /** Called once visible text has caught up while active is still true. */
  onCaughtUp?: () => void;
};

const DEFAULT_START_DELAY_MS = 100;
const DEFAULT_MS_PER_CHAR = 7;

const revealedPositions = new Map<string, number>();

export function clearTypewriterPosition(streamId: string) {
  revealedPositions.delete(streamId);
}

/**
 * Smooth letter-by-letter reveal using requestAnimationFrame (no setInterval jitter).
 * Target may grow over time (streaming); reveal pauses when caught up and resumes with new data.
 * When remounted mid-stream (e.g. after a chat switch), resumes from the last revealed position.
 */
export function useSmoothTypewriter(target: string, options: SmoothTypewriterOptions) {
  const {
    active,
    startDelayMs = DEFAULT_START_DELAY_MS,
    msPerChar = DEFAULT_MS_PER_CHAR,
    streamId,
    onCaughtUp,
  } = options;

  const savedPosition = streamId ? (revealedPositions.get(streamId) ?? 0) : 0;

  const [visible, setVisible] = useState(() =>
    savedPosition > 0 ? target.slice(0, savedPosition) : '',
  );
  const targetRef = useRef(target);
  const visibleLengthRef = useRef(savedPosition);
  const charBudgetRef = useRef(0);
  const streamStartRef = useRef<number | null>(savedPosition > 0 ? 0 : null);
  const revealStartRef = useRef<number | null>(savedPosition > 0 ? 0 : null);
  const lastFrameRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const caughtUpNotifiedRef = useRef(false);
  const onCaughtUpRef = useRef(onCaughtUp);

  targetRef.current = target;
  onCaughtUpRef.current = onCaughtUp;

  useEffect(() => {
    if (!active) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const resumed = streamId ? (revealedPositions.get(streamId) ?? 0) : 0;
    const isResume = resumed > 0;

    caughtUpNotifiedRef.current = false;

    if (isResume) {
      // Resume from the saved position — no reset, no start delay.
      visibleLengthRef.current = resumed;
      charBudgetRef.current = 0;
      streamStartRef.current = 0;
      revealStartRef.current = 0;
      lastFrameRef.current = null;
      setVisible(targetRef.current.slice(0, resumed));
    } else {
      // Fresh start.
      streamStartRef.current = null;
      revealStartRef.current = null;
      lastFrameRef.current = null;
      charBudgetRef.current = 0;
      visibleLengthRef.current = 0;
      setVisible('');
    }

    const frame = (now: number) => {
      const currentTarget = targetRef.current;

      if (currentTarget.length > 0 && streamStartRef.current === null) {
        streamStartRef.current = now;
      }

      if (streamStartRef.current !== null && revealStartRef.current === null) {
        if (now - streamStartRef.current < startDelayMs) {
          rafRef.current = requestAnimationFrame(frame);
          return;
        }
        revealStartRef.current = now;
        lastFrameRef.current = now;
      }

      if (revealStartRef.current !== null) {
        const last = lastFrameRef.current ?? now;
        const delta = Math.min(now - last, 48);
        lastFrameRef.current = now;
        charBudgetRef.current += delta / msPerChar;

        const targetLength = currentTarget.length;
        if (targetLength < visibleLengthRef.current) {
          visibleLengthRef.current = 0;
          charBudgetRef.current = 0;
        }

        let nextLength = visibleLengthRef.current;
        while (charBudgetRef.current >= 1 && nextLength < targetLength) {
          charBudgetRef.current -= 1;
          nextLength += 1;
        }

        if (nextLength !== visibleLengthRef.current) {
          visibleLengthRef.current = nextLength;
          if (streamId) revealedPositions.set(streamId, nextLength);
          setVisible(currentTarget.slice(0, nextLength));
        }

        if (nextLength >= targetLength && targetLength > 0 && !caughtUpNotifiedRef.current) {
          caughtUpNotifiedRef.current = true;
          onCaughtUpRef.current?.();
        }
      }

      rafRef.current = requestAnimationFrame(frame);
    };

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [active, streamId, startDelayMs, msPerChar]);

  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  return { visible, visibleRef };
}
